import dayjs from 'dayjs'
import { extractApiErrorMessage } from '../../../helpers/apiErrorMessage'
import { centsToDollarsDisplay } from '../../displayFormatters'
import {
  QboPaymentSnapshot,
  QuickBooksInvoicePayments,
  QuickBooksPaymentDiagnostic,
  QuickBooksPaymentIssue,
  QuickBooksPaymentMethodType,
  QuickBooksPaymentReapplyBlockedReason,
  QuickBooksPaymentRelinkCandidate,
  QuickBooksPaymentStatus,
} from './quickBooksResyncApi'

const NotAvailable = '—'

export type PaymentIssueChip = {
  label: string
  color: 'red' | 'orange' | 'green'
}

export type AppliedInvoiceView = {
  key: string
  label: string
  isThisInvoice: boolean
}

export type QboPaymentView = {
  exists: boolean
  customerName: string
  total: string
  unappliedAmount: string
  appliedInvoices: AppliedInvoiceView[]
  syncToken: string
  created: string
  lastUpdated: string
}

export type PaymentDiagnosticView = {
  key: string
  paymentId: string
  title: string
  method: string
  reference: string
  amount: string
  status: string
  recorded: string
  qboPaymentId: string
  qbo: QboPaymentView | null
  chips: PaymentIssueChip[]
  hasCustomerMismatch: boolean
  exportedToDifferentRealm: boolean
  syncToken: string
  reapplyUnavailableReason: string | null
}

export const defaultQboPaymentSnapshot = (): QboPaymentSnapshot => ({
  exists: true,
  qboCustomerId: '',
  qboCustomerName: '',
  total: 0,
  unappliedAmount: 0,
  appliedInvoices: [],
  syncToken: '0',
  createdAt: null,
  lastUpdatedAt: null,
})

export const defaultInvoicePayments = (): QuickBooksInvoicePayments => ({
  invoiceId: '',
  invoiceNumber: '',
  qboInvoiceId: '',
  qboDocNumber: null,
  qboCustomerId: '',
  qboCustomerName: '',
  payments: [],
})

export const defaultPaymentDiagnostic = (): QuickBooksPaymentDiagnostic => ({
  paymentId: '',
  methodType: 'check',
  checkNumber: '',
  lastFour: '',
  amount: 0,
  status: 'completed',
  paymentRecordedAt: null,
  qboPaymentId: '',
  qboPayment: null,
  issues: [],
  reapplyBlockedReason: 'notExported',
})

const methodLabels: Record<QuickBooksPaymentMethodType, string> = {
  unknown: 'Unknown method',
  card: 'Card',
  ach: 'ACH',
  cash: 'Cash',
  check: 'Check',
  validatedAch: 'Validated ACH',
}

const statusLabels: Record<QuickBooksPaymentStatus, string> = {
  ready: 'Ready',
  pending: 'Pending',
  completed: 'Completed',
  failed: 'Failed',
  canceled: 'Canceled',
}

const issueChips: Record<QuickBooksPaymentIssue, PaymentIssueChip> = {
  notExported: { label: 'Not exported', color: 'orange' },
  deletedInQbo: { label: 'Deleted in QBO', color: 'red' },
  notAppliedToInvoice: { label: 'Not applied to this invoice', color: 'red' },
  appliedToDifferentInvoice: { label: 'Applied to a different invoice', color: 'red' },
  customerMismatch: { label: 'Customer mismatch', color: 'red' },
  exportedToDifferentRealm: { label: 'Exported to a different QBO company', color: 'red' },
}

const reapplyBlockedReasons: Record<QuickBooksPaymentReapplyBlockedReason, string> = {
  notExported: 'This payment was never exported to QuickBooks.',
  deletedInQbo: 'The QuickBooks payment was deleted.',
  customerMismatch: "The QuickBooks payment belongs to a different customer than this invoice.",
  insufficientUnappliedAmount: "The QuickBooks payment doesn't have enough unapplied to cover this payment.",
  alreadyApplied: 'The QuickBooks payment is already applied to this invoice.',
  exportedToDifferentRealm: 'This payment was exported to a different QuickBooks company than the one now connected.',
}

export const getPaymentIssueChips = (issues: QuickBooksPaymentIssue[]): PaymentIssueChip[] => {
  if (issues.length === 0) {
    return [{ label: 'OK', color: 'green' }]
  }
  return issues.map(issue => issueChips[issue])
}

const formatDateTime = (value: string | null): string => {
  if (value === null) {
    return NotAvailable
  }
  return dayjs(value).format('MMM D, YYYY h:mm A')
}

const toReference = (payment: QuickBooksPaymentDiagnostic): string => {
  if (payment.checkNumber.trim() !== '') {
    return `#${payment.checkNumber}`
  }
  if (payment.lastFour.trim() !== '') {
    return `•••• ${payment.lastFour}`
  }
  return NotAvailable
}

const toTitle = (payment: QuickBooksPaymentDiagnostic): string => {
  const method = methodLabels[payment.methodType]
  if (payment.checkNumber.trim() !== '') {
    return `${method} #${payment.checkNumber}`
  }
  if (payment.lastFour.trim() !== '') {
    return `${method} ending ${payment.lastFour}`
  }
  return method
}

const toQboPaymentView = (snapshot: QboPaymentSnapshot, invoiceQboId: string): QboPaymentView => ({
  exists: snapshot.exists,
  customerName: snapshot.qboCustomerName.trim() === '' ? NotAvailable : snapshot.qboCustomerName,
  total: centsToDollarsDisplay(snapshot.total) ?? NotAvailable,
  unappliedAmount: centsToDollarsDisplay(snapshot.unappliedAmount) ?? NotAvailable,
  appliedInvoices: snapshot.appliedInvoices.map(applied => ({
    key: applied.qboInvoiceId,
    label: `${applied.docNumber.trim() === '' ? applied.qboInvoiceId : applied.docNumber} (${centsToDollarsDisplay(applied.amount)})`,
    isThisInvoice: applied.qboInvoiceId === invoiceQboId,
  })),
  syncToken: snapshot.syncToken,
  created: formatDateTime(snapshot.createdAt),
  lastUpdated: formatDateTime(snapshot.lastUpdatedAt),
})

export const toPaymentDiagnosticViews = (
  payments: QuickBooksPaymentDiagnostic[],
  invoiceQboId: string
): PaymentDiagnosticView[] => {
  return payments.map(payment => ({
    key: payment.paymentId,
    paymentId: payment.paymentId,
    title: toTitle(payment),
    method: methodLabels[payment.methodType],
    reference: toReference(payment),
    amount: centsToDollarsDisplay(payment.amount) ?? NotAvailable,
    status: statusLabels[payment.status],
    recorded: payment.paymentRecordedAt === null
      ? NotAvailable
      : dayjs(payment.paymentRecordedAt).format('MMM D, YYYY'),
    qboPaymentId: payment.qboPaymentId.trim() === '' ? 'Not exported' : payment.qboPaymentId,
    qbo: payment.qboPayment === null ? null : toQboPaymentView(payment.qboPayment, invoiceQboId),
    chips: getPaymentIssueChips(payment.issues),
    hasCustomerMismatch: payment.issues.includes('customerMismatch'),
    exportedToDifferentRealm: payment.issues.includes('exportedToDifferentRealm'),
    syncToken: payment.qboPayment?.syncToken ?? '',
    reapplyUnavailableReason: payment.reapplyBlockedReason === null
      ? null
      : reapplyBlockedReasons[payment.reapplyBlockedReason],
  }))
}

export const getInvoicePaymentsTitle = (invoiceNumber: string, qboDocNumber: string | null): string => {
  if (qboDocNumber === null || qboDocNumber.trim() === '') {
    return `Payments on invoice ${invoiceNumber}`
  }
  return `Payments on invoice ${invoiceNumber} (QBO invoice ${qboDocNumber})`
}

const qboInvoiceLabel = (qboDocNumber: string | null, qboInvoiceId: string): string => {
  if (qboDocNumber === null || qboDocNumber.trim() === '') {
    return qboInvoiceId
  }
  return qboDocNumber
}

export const isReapplyDisabled = (view: PaymentDiagnosticView, reapplyingPaymentId: string | undefined): boolean => {
  if (view.reapplyUnavailableReason !== null) {
    return true
  }
  return reapplyingPaymentId !== undefined && reapplyingPaymentId !== view.paymentId
}

export const getReapplyConfirmText = (
  view: PaymentDiagnosticView,
  qboDocNumber: string | null,
  qboInvoiceId: string
): string => {
  return `Apply QBO payment ${view.qboPaymentId} (${view.amount}) to invoice ${qboInvoiceLabel(qboDocNumber, qboInvoiceId)}`
}

export const getReapplySuccessMessage = (
  view: PaymentDiagnosticView,
  qboDocNumber: string | null,
  qboInvoiceId: string
): string => {
  return `Applied QBO payment ${view.qboPaymentId} (${view.amount}) to invoice ${qboInvoiceLabel(qboDocNumber, qboInvoiceId)}.`
}

export const getReapplyErrorMessage = (error: unknown): string => {
  return extractApiErrorMessage(error, 'Failed to re-apply the payment in QuickBooks. Please try again.')
}

export type PaymentRelinkCandidateRow = {
  key: string
  qboPaymentId: string
  customerName: string
  total: string
  unappliedAmount: string
  transactionDate: string
  reference: string
  appliedTo: string
  isCurrentLink: boolean
  linkedToInvoiceNumber: string | null
  matchTags: string[]
}

export const defaultPaymentRelinkCandidate = (): QuickBooksPaymentRelinkCandidate => ({
  qboPaymentId: '',
  customerName: '',
  total: 0,
  unappliedAmount: 0,
  transactionDate: null,
  paymentRefNumber: '',
  appliedInvoiceDocNumbers: [],
  linkedToInvoiceNumber: null,
  matchesAmount: false,
  matchesNote: false,
  matchesDate: false,
})

const toPaymentMatchTags = (candidate: QuickBooksPaymentRelinkCandidate): string[] => {
  const tags: string[] = []
  if (candidate.matchesAmount) {
    tags.push('Same amount')
  }
  if (candidate.matchesNote) {
    tags.push('Note references payment or invoice')
  }
  if (candidate.matchesDate) {
    tags.push('Date near recorded date')
  }
  return tags
}

export const toPaymentRelinkCandidateRows = (
  candidates: QuickBooksPaymentRelinkCandidate[],
  currentQboPaymentId: string
): PaymentRelinkCandidateRow[] => {
  return candidates.map(candidate => ({
    key: candidate.qboPaymentId,
    qboPaymentId: candidate.qboPaymentId,
    customerName: candidate.customerName.trim() === '' ? NotAvailable : candidate.customerName,
    total: centsToDollarsDisplay(candidate.total) ?? NotAvailable,
    unappliedAmount: centsToDollarsDisplay(candidate.unappliedAmount) ?? NotAvailable,
    transactionDate: candidate.transactionDate === null
      ? NotAvailable
      : dayjs(candidate.transactionDate).format('MMM D, YYYY'),
    reference: candidate.paymentRefNumber.trim() === '' ? NotAvailable : candidate.paymentRefNumber,
    appliedTo: candidate.appliedInvoiceDocNumbers.length === 0
      ? 'Unapplied'
      : candidate.appliedInvoiceDocNumbers.join(', '),
    isCurrentLink: currentQboPaymentId !== '' && candidate.qboPaymentId === currentQboPaymentId,
    linkedToInvoiceNumber: candidate.linkedToInvoiceNumber,
    matchTags: toPaymentMatchTags(candidate),
  }))
}

export const getNoPaymentCandidatesMessage = (qboCustomerName: string): string => {
  if (qboCustomerName.trim() === '') {
    return "The invoice's QuickBooks customer has no payments in the connected QuickBooks company."
  }
  return `QuickBooks customer ${qboCustomerName} has no payments in the connected QuickBooks company.`
}

export const paymentCandidateListTruncatedMessage =
  'Not every QuickBooks payment for this customer could be loaded — older payments are missing from this list.'

export const getPaymentRelinkSuccessMessage = (paymentTitle: string, qboPaymentId: string): string => {
  return `Relinked ${paymentTitle} to QBO payment ${qboPaymentId}.`
}

export const getPaymentRelinkCandidatesErrorMessage = (error: unknown): string => {
  return extractApiErrorMessage(error, 'Failed to look up QuickBooks payments for this customer. Try again.')
}

export const getPaymentRelinkErrorMessage = (error: unknown): string => {
  return extractApiErrorMessage(error, 'Failed to relink the payment. Please try again.')
}

export const noPaymentsMessage = 'This invoice has no SynkedUP payments.'

export const getInvoicePaymentsErrorMessage = (error: unknown): string => {
  return extractApiErrorMessage(error, "Failed to load the invoice's payments from QuickBooks. Try again.")
}
